// Signing the viewer in to the app runtime (POST /__session), and doing it EARLY (Board #2594): the
// session is on an app's critical path (the frame needs its cookie), so the shell signs in on idle
// and when the pointer reaches the Apps nav item, and every page reuses that sign-in for as long as
// the cortex token it carried is the current one. Light on purpose: the shell imports it.
import { getAccessToken } from '../platform/index.ts';
import { config } from '../platform/index.ts';
import { resolveRuntimeBase } from './runtime-base.ts';

/**
 * The runtime's origin: the one the web server wrote into the page (`<meta name="app-runtime-origin">`,
 * from WEB_PROTOTYPE_RUNTIME_ORIGIN: one image for every environment), else the build's
 * VITE_PROTOTYPE_RUNTIME_URL (`/` = this page's origin, a project's standalone shell).
 */
export const runtimeBase = () =>
  resolveRuntimeBase(config.VITE_PROTOTYPE_RUNTIME_URL, document, window.location.origin);

/** Does this deployment have an app runtime (the server's meta or the build's URL)? */
export const hasRuntime = () => runtimeBase() !== '';

// Set when a sign-in could not reach the runtime at all (not running, a network error): the shell's
// early sign-ins stop for this page load, so a runtime that is down costs one failed request, not one per
// idle moment or pointer move. A page that shows an app still asks (ensureRuntimeSession).
let unreachable = false;

/**
 * Signs the viewer in to the runtime: `POST /__session` with the cortex token sets the runtime's
 * HttpOnly session cookie, which the preview iframe then carries (no second login). Needs the runtime
 * on the same site as this UI and this origin in its WEB_ORIGINS.
 */
export async function runtimeSession(): Promise<void> {
  const t = getAccessToken();
  // The runtime makes a session only from a bearer: without a token a POST is a certain 401 (a page
  // whose api calls are authorized another way, e.g. the live e2e's injected token), so none is sent.
  if (!t) throw new Error('The app runtime needs a signed-in cortex token');
  if (!hasRuntime()) throw new Error('This deployment has no app runtime');
  let res: Response;
  try {
    res = await fetch(`${runtimeBase()}/__session`, {
      method: 'POST',
      credentials: 'include',
      headers: { authorization: `Bearer ${t}` },
    });
  } catch (e) {
    unreachable = true;
    throw new Error(`The app runtime cannot be reached: ${(e as Error).message}`);
  }
  unreachable = false;
  // Read to its end (it is empty): an answer left unread has its stream cancelled, which the browser
  // reports as an aborted request (Board #2594).
  await res.text().catch(() => '');
  if (res.status !== 204) throw new Error(`The app runtime refused the session (${res.status})`);
}

// The sign-in made with the current token: the runtime's cookie holds that token (a browser-session
// cookie), so it stays good until the token changes. A refused or failed one is forgotten.
let signed: { token: string; done: Promise<void> } | null = null;

/**
 * The runtime session for the current cortex token: the one already made (or in flight) when the token
 * is the same, else a new `POST /__session`. `fresh` posts again regardless (a retry after a failure,
 * or after the app signed out in its frame).
 */
export function ensureRuntimeSession(o: { fresh?: boolean } = {}): Promise<void> {
  const token = getAccessToken();
  if (!o.fresh && token && signed?.token === token) return signed.done;
  const done = runtimeSession();
  if (!token) return done;
  const entry = { token, done };
  signed = entry;
  done.catch(() => {
    if (signed === entry) signed = null;
  });
  return done;
}

/** Signs in ahead of need (the shell's idle time, the pointer on the Apps item); a failure waits for the page. */
export function warmRuntimeSession(): void {
  // No runtime here, or one that could not be reached: never a request the page did not need.
  if (!getAccessToken() || !hasRuntime() || unreachable) return;
  void ensureRuntimeSession().catch(() => undefined);
}

/**
 * Keeps the runtime signed in while a page shows an app: the cookie holds the cortex access token, so
 * each refreshed token goes to the runtime too (checked every 20 s). Answers the stop function.
 */
export function keepRuntimeSession(): () => void {
  let token = getAccessToken();
  const t = setInterval(() => {
    if (!hasRuntime()) return;
    const now = getAccessToken();
    if (now && now !== token) {
      token = now;
      void ensureRuntimeSession().catch(() => undefined);
    }
  }, 20_000);
  return () => clearInterval(t);
}
